import omitBy from 'lodash/omitBy'
import pick from 'lodash/pick'
import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import querystring from 'querystring'
import appendQuery from 'append-query'
import RequestError from './RequestError'
import ErrorCodeHandler from './ErrorCodeHandler'
import { merge } from '../utils/commonUtils'

const CALLBACK = 'handleCallback'
const METHODS_WITH_BODY = ['post', 'put', 'patch']
const XSRF_TOKEN_COOKIE_KEY = 'PASSPORT_CSRF_TOKEN'
const XSRF_TOKEN_HEADER_KEY = 'X-XSRF-TOKEN'
const DEFAULT_FETCH_CONFIG = {
  /*
   * Base on current design (jsonp solution),
   * force to use this header
   */
  headers: {
    Accept: 'text/html',
    'Content-type': 'application/x-www-form-urlencoded',
  },
  /*
   * Base on current design (jsonp solution),
   * force to use this data config
   */
  data: {
    request_type: 'JSONP',
    featureResponseType: 'JSONP',
    callback: CALLBACK,
  },

  withCredentials: true,
}

const callbackRemover = (() => {
  const reg = new RegExp(`^${CALLBACK}\\(|\\);$`, 'g')
  return str => {
    const result = str.replace(reg, '')
    return result ? result.replace(/^'|'$/g, '') : undefined
  }
})()

const getCookieValue = cookieKey => {
  if (typeof document === 'undefined' || !document.cookie) {
    return ''
  }

  const encodedKey = encodeURIComponent(cookieKey)
  const cookie = document.cookie
    .split(';')
    .map(item => item.trim())
    .find(item => item.startsWith(`${encodedKey}=`) || item.startsWith(`${cookieKey}=`))

  if (!cookie) {
    return ''
  }

  const rawValue = cookie.slice(cookie.indexOf('=') + 1)
  try {
    return decodeURIComponent(rawValue)
  } catch (_) {
    return rawValue
  }
}

const isPassportAPIRequest = (requestPath, passportAPIServerHost) => {
  if (/\/v5\/|\/auth\/realms\//i.test(String(requestPath || ''))) {
    return false
  }

  const host = passportAPIServerHost || window.passportAPIServerHost

  if (!host) {
    return false
  }

  const base = window.location.origin

  try {
    return new URL(requestPath, base).origin === new URL(host, base).origin
  } catch (_) {
    return false
  }
}

const buildFetchInit = fetchConfig => {
  const {
    data,
    withCredentials,
    _ignoreTrim,
    passportAPIServerHost,
    ...rest
  } = fetchConfig

  return {
    ...rest,
    credentials: withCredentials ? 'include' : 'same-origin',
  }
}

const parseResponseData = async response => {
  try {
    const contentType = response.headers.get('content-type') || ''

    if (response.status === 204 || response.status === 205) {
      return undefined
    }

    if (contentType.includes('application/json')) {
      return await response.json()
    }

    return response.text()
  } catch (_) { 
    return undefined
  }
}

const requestWithMethod = (method, path, fetchConfig) => {
  let url = path
  const _fetchConfig = merge({ method }, DEFAULT_FETCH_CONFIG, fetchConfig)
  const xsrfToken = getCookieValue(XSRF_TOKEN_COOKIE_KEY)
  let { data } = _fetchConfig
  const { _ignoreTrim, passportAPIServerHost } = _fetchConfig

  if (xsrfToken && isPassportAPIRequest(path, passportAPIServerHost)) {
    _fetchConfig.headers = {
      ..._fetchConfig.headers,
      [XSRF_TOKEN_HEADER_KEY]: xsrfToken,
    }
  }

  data = omitBy(data, isNil)
  !_ignoreTrim && Object.keys(data).forEach(k => {
    if (data[k] && data[k].trim) {
      data[k] = data[k].trim()
    }
  })

  if (method === 'get' || method === 'delete') {
    /** FireFox ajax get request can not have a data, so let it be `null`  */
    _fetchConfig.data = undefined
    url = appendQuery(path, { ...data, _: new Date().getTime() })
  } else if (METHODS_WITH_BODY.includes(method)) {
    if (data.postResource === 'wallet') {
      const filterData = isEmpty(data.allowedParams) ? data : pick(data, data.allowedParams)
      _fetchConfig.body = JSON.stringify(filterData)
    } else {
      _fetchConfig.body = querystring.stringify(data)
    }
  } else {
    _fetchConfig.body = undefined
  }

  const requestInit = buildFetchInit(_fetchConfig)

  function mapResponse(response) {
    const { status } = response
    let resp
    let parseError
    let jsonResp
    if (typeof response.data === 'string') {
      resp = callbackRemover(response.data)
      if (resp) {
        try {
          jsonResp = JSON.parse(resp)
        } catch (e) {
          parseError = new RequestError('FAILED')
        }
      }
    } else {
      // typeof data is 'object'
      resp = response.data
      jsonResp = response.data
    }

    const statusCode = parseInt(status / 100, 10)
    if (statusCode === 2 || statusCode === 3) {
      return jsonResp || resp || {}
    } else {
      throw (parseError || new RequestError(status))
    }
  }

  function mapError(error) {
    const errMsg = ErrorCodeHandler.handleGeneral(error && error.response && error.response.data)
    throw new RequestError('FAILED', errMsg)
  }

  return fetch(url, requestInit)
    .then(async response => {
      const responseData = await parseResponseData(response)
      const responseLike = {
        status: response.status,
        data: responseData,
      }

      if (response.ok) {
        return mapResponse(responseLike)
      }

      throw {
        response: responseLike,
      }
    })
    .catch(error => {
      if (error instanceof RequestError) {
        throw error
      }

      if (error && error.response) {
        return mapError(error)
      }

      return mapError({
        response: {
          data: error && error.message,
        },
      })
    })
}

export default ['get', 'post', 'put', 'patch', 'delete', 'head'].reduce(
  (api, method) => ({
    ...api,
    [method]: requestWithMethod.bind(null, method),
  }),
  {},
)
